// src/components/TZMMS-INV/constants/tzmmsInvUi.js
// ============================================================================
// TZMMS Inventory Counter — UI / scanner / email constants
// ----------------------------------------------------------------------------
// PURPOSE
// - Central place for the values you are most likely to tune later.
// - Change values here first before editing component logic.
//
// HOW TO EDIT SAFELY
// - Scanner behavior changes belong in BLOCK TZI.CON.002.
// - List box layout changes belong in BLOCK TZI.CON.003.
// - Email defaults belong in BLOCK TZI.CON.004.
// ============================================================================

// ============================================================================
// BLOCK TZI.CON.001 — Page identity constants
// ----------------------------------------------------------------------------
// EFFECT OF CHANGES
// - PAGE_TITLE changes the user-facing page title.
// - PAGE_ROUTE_PATH must match the React route + sidebar navigation target.
// - PAGE_PERMISSION_KEY should match the permission added to your app.
// ============================================================================
export const PAGE_TITLE = 'TZMMS Inventory Counter';
export const PAGE_ROUTE_PATH = '/tzmms-inv';
export const PAGE_PERMISSION_KEY = 'tzmms-inv';
export const PAGE_MAX_WIDTH = 'md';
// Mobile-optimized padding: responsive spacing for different screen sizes
export const PAGE_PADDING_MOBILE = 1.5;
export const PAGE_PADDING_DESKTOP = 3;

// ============================================================================
// BLOCK TZI.CON.002 — Scanner behavior constants
// ----------------------------------------------------------------------------
// EFFECT OF CHANGES
// - QR_SCANNER_FORMATS: supported 2D formats (QR, Data Matrix, Aztec, EAN-13, Code128).
//   Samsung S24+ devices benefit from expanded format support and continuous focus.
// - QR_SCANNER_SCAN_DELAY_MS: lower = faster repeated detection, but may create
//   more accidental duplicate scans if users hold a code in view.
// - QR_SCANNER_REPEAT_BLOCK_MS: same scanned text inside this time window is
//   ignored to prevent rapid-fire duplicates from one held QR code.
// - QR_SCANNER_ALLOW_DUPLICATE_VALUES: true lets the same QR text be counted
//   multiple times across the session. This is usually correct for inventory.
// - QR_SCANNER_COMPONENTS toggles built-in scanner controls (finder, torch, zoom).
// - QR_SCANNER_CONSTRAINTS includes focusMode: 'continuous' for Samsung/Android focus.
// ============================================================================
export const QR_SCANNER_FORMATS = ['qr_code', 'data_matrix', 'aztec', 'ean_13', 'code_128'];
export const QR_SCANNER_SCAN_DELAY_MS = 1000;
export const QR_SCANNER_REPEAT_BLOCK_MS = 4000;
export const QR_SCANNER_ALLOW_DUPLICATE_VALUES = false;
export const QR_SCANNER_AUTO_START_PAUSED = false;
// Scanner constraints with focus mode for Samsung S24 and other Android devices.
// focusMode: 'continuous' enables autofocus; camera picker for Samsung multi-lens support.
export const QR_SCANNER_CONSTRAINTS = {
  facingMode: { ideal: 'environment' }, // Rear camera preference with fallback.
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  // focusMode: 'continuous' signals autofocus preference to the browser.
  // Support varies by device/browser; not all phones honor this constraint.
  focusMode: 'continuous', // Samsung S24+ autofocus support.
};
export const QR_SCANNER_COMPONENTS = {
  finder: true, // Draw a visible finder box for the user.
  torch: true, // Allow flashlight when device/browser supports it.
  zoom: true, // Allow zoom when device/browser supports it.
  onOff: true, // Show built-in camera on/off control.
};

// ============================================================================
// BLOCK TZI.CON.003 — List box layout constants
// ----------------------------------------------------------------------------
// EFFECT OF CHANGES
// - INVENTORY_LIST_PANEL_HEIGHT_DESKTOP: list height on screens ≥ md breakpoint.
// - INVENTORY_LIST_PANEL_HEIGHT_MOBILE: list height on mobile (iPhone 16 ~390px).
// - INVENTORY_LIST_ROW_MIN_HEIGHT: touch target size (44px per Apple HIG).
// - INVENTORY_LIST_SHOW_TIMESTAMP toggles the smaller scanned-at text.
// ============================================================================
export const INVENTORY_LIST_PANEL_HEIGHT_DESKTOP = 420;
export const INVENTORY_LIST_PANEL_HEIGHT_MOBILE = 280;
export const INVENTORY_LIST_ROW_MIN_HEIGHT = 44;
export const INVENTORY_LIST_SHOW_TIMESTAMP = true;
// Legacy constant for backward compatibility (uses desktop height)
export const INVENTORY_LIST_PANEL_HEIGHT = INVENTORY_LIST_PANEL_HEIGHT_DESKTOP;

// ============================================================================
// BLOCK TZI.CON.004 — Email defaults
// ----------------------------------------------------------------------------
// EFFECT OF CHANGES
// - EMAIL_SUBJECT_PREFIX changes the default email subject line.
// - EMAIL_EMPTY_LIST_BLOCK prevents accidental blank emails.
// - EMAIL_RECIPIENT_OVERRIDE forces all emails to one address when populated.
//   Leave it blank to send to the logged-in user's email passed into the page.
// ============================================================================
export const EMAIL_SUBJECT_PREFIX = 'TZMMS Inventory Counter';
export const EMAIL_EMPTY_LIST_BLOCK = true;
export const EMAIL_RECIPIENT_OVERRIDE = '';
